import { useEffect, useRef, useState, useCallback } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";

import ErrorExtractor from "../common/error_extractor";
import { ReactComponent as UploadIc } from "../../assets/svg/upload.svg";
import {
  submitProductAPI,
  updateProductAPI,
  getProductEditApi,
  addProductAttachmentApi,
} from "./product-api";
import SearchWithFreeText from "../common/search-free-text";
import "../common/skeleton-effect.css";
import CommonSnackbar from "../common/reusable-commponent";
import {
  sanitizeInput,
  sanitizeNumericInput,
  sanitizeTextarea,
  validateFileType,
  validateFileSize,
} from "../common/security-utils";

const apiUrl = process.env.REACT_APP_API_URL;

/** Empty dimension template */
const EMPTY_DIMENSION = {
  length: "",
  breadth: "",
  height: "",
  quantity: "",
  price: "",
};

/** Allowed image MIME types */
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/** Maximum file size in MB */
const MAX_FILE_SIZE_MB = 5;

/** Maximum text input length */
const MAX_INPUT_LENGTH = 200;

/** Maximum description length */
const MAX_DESCRIPTION_LENGTH = 2000;

export const ProductCreate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  // --- Form state ---
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState(null);
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [searchProduct, setSearchProduct] = useState("");
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [weightOption, setWeightOption] = useState("");
  const [statusOption, setStatusOption] = useState("true");
  const [defaultValue, setDefaultValue] = useState(false);
  const [description, setDescription] = useState("");

  // --- Dimension state ---
  const [dimensionUnit, setDimensionUnit] = useState("");
  const [boxDimensionUnit, setBoxDimensionUnit] = useState("");
  const [customDimensionUnit, setCustomDimensionUnit] = useState("");
  const [singleDimension, setSingleDimension] = useState({ ...EMPTY_DIMENSION });
  const [boxDimension, setBoxDimension] = useState({ ...EMPTY_DIMENSION });
  const [customDimension, setCustomDimension] = useState({ ...EMPTY_DIMENSION });

  // --- File state ---
  const [files, setFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const fileInputRef = useRef(null);

  // --- UI state ---
  const [isLoader, setIsLoader] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errorMsg, setErrorMsg] = useState({});

  // --- Snackbar state ---
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });

  const handleSnackbarClose = useCallback(
    () => setSnackbar((prev) => ({ ...prev, show: false })),
    [],
  );

  const triggerSnackbar = useCallback((message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  }, []);

  // --- File handlers ---
  const processFiles = useCallback((selectedFiles) => {
    const validFiles = selectedFiles.filter((file) => {
      if (!validateFileType(file, ALLOWED_IMAGE_TYPES)) return false;
      if (!validateFileSize(file, MAX_FILE_SIZE_MB)) return false;
      return true;
    });

    if (validFiles.length < selectedFiles.length) {
      triggerSnackbar(
        `${selectedFiles.length - validFiles.length} file(s) skipped (invalid type or too large)`,
        false,
      );
    }

    const mapped = validFiles.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));

    setFiles((prev) => [...prev, ...mapped]);
  }, [triggerSnackbar]);

  const handleFileSelect = useCallback((e) => {
    processFiles(Array.from(e.target.files));
  }, [processFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    processFiles(Array.from(e.dataTransfer.files));
  }, [processFiles]);

  const removeImage = useCallback((index) => {
    setFiles((prev) => {
      const target = prev[index];
      if (target?.id) {
        setRemovedAttachmentIds((ids) => [...ids, target.id]);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // --- Dropdown handlers ---
  const handleDropdownProductChange = useCallback((option) => {
    setSelectedProductType(option);
    setSearchProduct(option.value ?? "");
  }, []);

  const handleSearchProductChange = useCallback((value) => {
    setSearchProduct(value);
    setSelectedProductType({ label: value, value });
  }, []);

  const handleDropdownCriteriaChange = useCallback((option) => {
    setSelectedCriteria(option);
    setSearchCriteria(option.value ?? "");
  }, []);

  const handleSearchCriteriaChange = useCallback((value) => {
    setSearchCriteria(value);
    setSelectedCriteria({ label: value, value });
  }, []);

  // --- Fetch product for edit ---
  const getProductEdit = useCallback(async (pid) => {
    if (!pid) return;
    setIsLoadingData(true);
    try {
      const response = await getProductEditApi(pid);
      if (response?.data?.success) {
        const p = response.data.products ?? {};
        setProductId(p.item_code ?? "");
        setProductName(p.item_name ?? "");
        setWeightOption(p.weight ?? "");
        setSelectedProductType(p.type ? { value: p.type, label: p.type } : null);
        setSearchProduct(p.type ?? "");
        setSelectedCriteria(p.criteria ? { value: p.criteria, label: p.criteria } : null);
        setSearchCriteria(p.criteria ?? "");
        setStatusOption(String(p.status));
        const single = p.uoms?.find((u) => u.uom_name === "Single") || {};
        const box = p.uoms?.find((u) => u.uom_name === "Box") || {};
        const custom = p.uoms?.find((u) => u.uom_name === "Custom") || {};
        setDimensionUnit(single.measurement_type ?? "");
        setBoxDimensionUnit(box.measurement_type ?? "");
        setCustomDimensionUnit(custom.measurement_type ?? "");
        setDefaultValue(p.default ?? false);

        setSingleDimension({
          length: single.length ?? "",
          breadth: single.width ?? "",
          height: single.height ?? "",
          quantity: single.quantity ?? "",
          price: single.price ?? "",
        });

        setBoxDimension({
          length: box.length ?? "",
          breadth: box.width ?? "",
          height: box.height ?? "",
          quantity: box.quantity ?? "",
          price: box.price ?? "",
        });

        setCustomDimension({
          length: custom.length ?? "",
          breadth: custom.width ?? "",
          height: custom.height ?? "",
          quantity: custom.quantity ?? "",
          price: custom.price ?? "",
        });

        setDescription(p.details ?? "");
        setFiles(
          (p.product_attachments ?? []).map((att) => ({
            file: null,
            preview: att.file_path,
            name: att.file_path?.split("/").pop().split("?")[0],
            id: att.id,
          })),
        );
      }
    } catch {
      // Error handled by api-service
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (id != null) getProductEdit(id);
  }, [id, getProductEdit]);

  // --- Build UOM payload ---
  const buildUOM = useCallback(
    (name, dimension) => ({
      uom_name: sanitizeInput(name),
      quantity: Number(sanitizeNumericInput(dimension.quantity)) || 0,
      price: Number(sanitizeNumericInput(dimension.price)) || 0,
      measurement_type: sanitizeInput(
        name === "Single" ? dimensionUnit : name === "Box" ? boxDimensionUnit : customDimensionUnit,
      ),
      length: Math.round(Number(sanitizeNumericInput(dimension.length)) || 0),
      width: Math.round(Number(sanitizeNumericInput(dimension.breadth)) || 0),
      height: Math.round(Number(sanitizeNumericInput(dimension.height)) || 0),
    }),
    [dimensionUnit, boxDimensionUnit, customDimensionUnit],
  );

  // --- File to base64 ---
  const fileToBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      }),
    [],
  );

  // --- Submit form ---
  const submitForm = useCallback(async () => {
    setIsLoader(true);
    setErrorMsg({});

    try {
      const payload = {
        item_code: productId ?? "",
        item_name: productName ?? "",
        weight: weightOption || "0",
        type: selectedProductType?.value ?? "",
        criteria: selectedCriteria?.value ?? "",
        status: statusOption != null ? statusOption === "true" : true,
        uoms: [
          buildUOM("Single", singleDimension),
          buildUOM("Box", boxDimension),
          buildUOM("Custom", customDimension),
        ],
        details: description ?? "",
        default: defaultValue,
        remove_attachments: removedAttachmentIds,
      };

      const response =
        id == null
          ? await submitProductAPI(payload)
          : await updateProductAPI(id, payload);

      if (response?.data?.success === true) {
        const newProductId = response.data.id;

        // Filter for new files only (those with a 'file' property)
        const newFiles = files.filter((item) => item.file);

        triggerSnackbar(response.data.message || "Saved", true);
        setIsLoader(false);

        if (newFiles.length > 0) {
          const attachmentsBase64 = await Promise.all(
            newFiles.map(async (item) => await fileToBase64(item.file)),
          );
          await addProductAttachmentApi(newProductId, {
            attachments: attachmentsBase64,
          });
        }
        navigate(-1);
      } else if (response?.status === 400 || response?.data?.error) {
        const backendError = response.data?.error;
        if (backendError) {
          const formattedErrors = Object.fromEntries(
            Object.entries(backendError).map(([key, value]) => [key, value[0]]),
          );
          setErrorMsg(formattedErrors);
        }
      } else {
        triggerSnackbar("Unexpected error", false);
      }
    } catch (err) {
      const apiError = err?.response?.data;
      if (apiError) {
        const extractor = new ErrorExtractor();
        const parsedErrors = extractor.extractErrors(
          apiError.error || apiError.errors || apiError.message || {},
          {},
        );
        setErrorMsg(parsedErrors);
        triggerSnackbar(sanitizeInput(apiError.message || "An error occurred"), false);
      } else {
        triggerSnackbar("An unexpected error occurred", false);
      }
    } finally {
      setIsLoader(false);
    }
  }, [
    id,
    productId,
    productName,
    weightOption,
    selectedProductType,
    selectedCriteria,
    statusOption,
    singleDimension,
    boxDimension,
    customDimension,
    dimensionUnit,
    boxDimensionUnit,
    customDimensionUnit,
    description,
    defaultValue,
    removedAttachmentIds,
    files,
    buildUOM,
    fileToBase64,
    navigate,
    triggerSnackbar,
  ]);

  // --- Reusable dimension section renderer ---
  const renderDimensionSection = useCallback(
    (title, dimension, setDimension, unit, setUnit) => (
      <div className="bg-white rounded-4 p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fs-14 fw-700">{title}</span>
          <select
            className="form-select"
            style={{ width: "100px", height: "32px" }}
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          >
            <option value="ft">ft</option>
            <option value="inch">inch</option>
            <option value="cm">cm</option>
          </select>
        </div>

        <div className="d-flex flex-wrap w-100 gap-2">
          {["length", "breadth", "height"].map((k) => (
            <div key={k} style={{ flex: 1, minWidth: "120px" }} className="text-start">
              <label className="form-label fs-12 fw-400 text-muted">
                {k.charAt(0).toUpperCase() + k.slice(1)}
              </label>
              <input
                type="number"
                placeholder="Enter here"
                className="form-control rounded-3"
                value={dimension[k]}
                onChange={(e) =>
                  setDimension((prev) => ({
                    ...prev,
                    [k]: e.target.value,
                  }))
                }
                style={{ height: "36px" }}
              />
            </div>
          ))}

          <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
            <label className="form-label fs-12 fw-400">Qty</label>
            <input
              type="number"
              placeholder="Enter here"
              className="form-control rounded-3"
              value={dimension.quantity}
              onChange={(e) =>
                setDimension((prev) => ({
                  ...prev,
                  quantity: e.target.value,
                }))
              }
              style={{ height: "36px" }}
            />
          </div>

          <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
            <label className="form-label fs-12 fw-400">Price</label>
            <input
              type="number"
              placeholder="Enter here"
              className="form-control rounded-3"
              value={dimension.price}
              onChange={(e) =>
                setDimension((prev) => ({
                  ...prev,
                  price: e.target.value,
                }))
              }
              style={{ height: "36px" }}
            />
          </div>
        </div>
      </div>
    ),
    [],
  );

  return (
    <div>
      <div className="px-2 d-flex align-items-center justify-content-between">
        <div className="d-flex gap-3 my-3">
          <MoveLeft className="cursor-pointer" onClick={() => navigate(-1)} />
          <p className="fw-semibold m-0">{id ? "Edit Product" : "New Product"}</p>
        </div>

        <div className="d-flex gap-3">
          <Button
            className="cursor-pointer bg-white rounded-pill px-4 py-2 btn-border-grey fs-15 fw-500"
            style={{ border: "1px solid #D4DDE7", color: "#212129" }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>

          <Button
            className={`cursor-pointer dark-purple-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
            onClick={submitForm}
            disabled={isLoader}
            style={{ minWidth: "100px" }}
          >
            {isLoader ? <Spinner animation="border" size="sm" /> : id ? "Update" : "Save"}
          </Button>
        </div>
      </div>

      <div>
        {isLoadingData ? (
          <div className="row">
            <div className="col-12 col-lg-8">
              {/* Product Details Skeleton */}
              <div className="bg-white rounded-4 p-3 mb-3">
                <div className="row">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="col-6 mb-3 text-start">
                      <div className="skeleton skeleton-text-sm mb-2" style={{ width: "80px" }} />
                      <div className="skeleton" style={{ width: "100%", height: "36px", borderRadius: "8px" }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Dimension Skeletons */}
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="bg-white rounded-4 p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="skeleton skeleton-text" style={{ width: "180px", height: "14px" }} />
                    <div className="skeleton" style={{ width: "100px", height: "32px", borderRadius: "8px" }} />
                  </div>
                  <div className="d-flex flex-wrap w-100 gap-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} style={{ flex: 1, minWidth: "120px" }} className="text-start">
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "36px", borderRadius: "8px" }} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="col-12 col-lg-4">
              {/* Product Images Skeleton */}
              <div className="bg-white rounded-4 p-3 mb-3">
                <div className="skeleton skeleton-text mb-2" style={{ width: "120px" }} />
                <div
                  className="border rounded"
                  style={{ minHeight: "140px", borderStyle: "dashed", background: "#f8f9fa" }}
                >
                  <div className="d-flex flex-column align-items-center justify-content-center h-100 py-4">
                    <div className="skeleton" style={{ width: "32px", height: "32px", borderRadius: "50%", marginBottom: "10px" }} />
                    <div className="skeleton skeleton-text-sm" style={{ width: "140px" }} />
                  </div>
                </div>
              </div>

              {/* Description Skeleton */}
              <div className="bg-white rounded-4 p-3 mb-3">
                <div className="skeleton skeleton-text mb-2" style={{ width: "100px" }} />
                <div className="skeleton" style={{ width: "100%", height: "100px", borderRadius: "8px" }} />
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-12 col-lg-8">
              <div className="bg-white rounded-4 p-3 mb-3">
                <div className="row">
                  <div className="col-6 text-start">
                    <label className="form-label fs-12 fw-400">
                      Product ID <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3"
                      value={productId}
                      placeholder="Enter here"
                      maxLength={MAX_INPUT_LENGTH}
                      style={{ height: "36px" }}
                      onChange={(e) => setProductId(e.target.value)}
                    />
                    <div className="text-danger fs-12">{errorMsg.item_code}</div>
                  </div>

                  <div className="col-6 text-start">
                    <label className="form-label fs-12 fw-400">
                      Product Name<span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter here"
                      className="form-control rounded-3"
                      maxLength={MAX_INPUT_LENGTH}
                      style={{ height: "36px" }}
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                    />
                    <div className="text-danger fs-12">{errorMsg.item_name}</div>
                  </div>

                  <div className="col-6 mt-3 text-start">
                    <label className="form-label fs-12 fw-400">Weight</label>
                    <input
                      type="number"
                      className="form-control rounded-3"
                      value={weightOption}
                      style={{ height: "36px" }}
                      placeholder="Enter here"
                      onChange={(e) => setWeightOption(e.target.value)}
                    />
                    <div className="text-danger fs-12">{errorMsg.weight}</div>
                  </div>

                  <div className="col-6 mt-3 text-start">
                    <label className="form-label fs-12 fw-400">
                      Type<span style={{ color: "red" }}>*</span>
                    </label>
                    <SearchWithFreeText
                      apiUrl={`${apiUrl}get_product_types`}
                      placeholder="Search & type..."
                      selectedValue={selectedProductType}
                      onDropdownChange={handleDropdownProductChange}
                      onChangeSearch={handleSearchProductChange}
                    />
                    <div className="text-danger fs-12">{errorMsg.type}</div>
                  </div>

                  <div className="col-6 mt-3 text-start">
                    <label className="form-label fs-12 fw-400">Criteria</label>
                    <SearchWithFreeText
                      apiUrl={`${apiUrl}products/criteria`}
                      placeholder="Search & type..."
                      selectedValue={selectedCriteria}
                      onDropdownChange={handleDropdownCriteriaChange}
                      onChangeSearch={handleSearchCriteriaChange}
                    />
                    <div className="text-danger fs-12">{errorMsg.criteria}</div>
                  </div>

                  <div className="col-6 mt-3 text-start">
                    <label className="form-label fs-12 fw-400">Status</label>
                    <select
                      className="form-select rounded-3"
                      value={statusOption}
                      onChange={(e) => setStatusOption(e.target.value)}
                    >
                      <option value="true">Active</option>
                      <option value="false">InActive</option>
                    </select>
                    <div className="text-danger fs-12">{errorMsg?.status}</div>
                  </div>

                  <div className="col-6 mt-3 text-start d-flex align-items-baseline gap-2">
                    <div
                      className="d-flex align-items-center cursor-pointer gap-2"
                      onClick={() => setDefaultValue(!defaultValue)}
                    >
                      <input
                        type="checkbox"
                        checked={defaultValue}
                        onChange={(e) => setDefaultValue(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label className="form-label fs-12 fw-400 mb-0 cursor-pointer">
                        Default
                      </label>
                    </div>
                    <div className="text-danger fs-12 fw-400">{errorMsg?.default}</div>
                  </div>
                </div>
              </div>

              {renderDimensionSection(
                "Single Product Dimension",
                singleDimension,
                setSingleDimension,
                dimensionUnit,
                setDimensionUnit,
              )}
              {renderDimensionSection(
                "Box Product Dimension",
                boxDimension,
                setBoxDimension,
                boxDimensionUnit,
                setBoxDimensionUnit,
              )}
              {renderDimensionSection(
                "Custom Product Dimension",
                customDimension,
                setCustomDimension,
                customDimensionUnit,
                setCustomDimensionUnit,
              )}
            </div>

            <div className="col-12 col-lg-4">
              <div className="bg-white rounded-4 p-3 mb-3 text-start">
                <div className="fs-14 fw-700 mb-2">Product Images</div>
                <div
                  className="border rounded d-flex flex-column align-items-center justify-content-center p-4 text-center"
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderColor: "#dee2e6",
                    minHeight: "140px",
                    borderStyle: "dashed",
                    cursor: "pointer",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <div className="mb-2 text-primary">
                    <UploadIc width="32" height="32" />
                  </div>
                  <div className="fs-12 fw-500 mb-1 text-primary">Click or Drop images here</div>
                  <div className="text-muted" style={{ fontSize: "10px" }}>
                    Upload multiple images (JPEG, PNG, GIF, WebP — max {MAX_FILE_SIZE_MB}MB each)
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                </div>

                {files.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-3 w-100">
                    {files.map((item, index) => (
                      <div key={index} className="position-relative" style={{ width: 90, height: 90 }}>
                        {item.preview ? (
                          <img
                            src={item.preview}
                            alt="preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "6px",
                            }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded">
                            {sanitizeInput(item.file?.name ?? item.name ?? "")}
                          </div>
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(index);
                          }}
                          className="btn btn-danger btn-sm position-absolute"
                          style={{
                            top: "-6px",
                            right: "-6px",
                            padding: "2px 6px",
                            fontSize: "10px",
                            borderRadius: "50%",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-4 p-3 mb-3 text-start">
                <div className="fs-14 fw-700 mb-2">Description</div>
                <textarea
                  className="form-control rounded-3"
                  rows={4}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ---------- Snackbar ---------- */}
      <CommonSnackbar
        show={snackbar.show}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        isSuccess={snackbar.status}
      />
    </div>
  );
};

export default ProductCreate;
